import { graphql, Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import * as React from 'react';
import styled from 'styled-components';
import useElementSize from '../hooks/useElementSize';
import { cssClampValue, getSeoFriendlyAlt } from '../utils/common';
import DefaultPlaylistImage from './DefaultPlaylistImage';

const Wrapper = styled.div`
  position: relative;
  border-radius: 10px;
  isolation: isolate;
  overflow: hidden;
  aspect-ratio: 1/1;

  &:focus-within {
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
  }
  > div,
  a {
    display: block;
    padding: ${cssClampValue(16, 30)};
    text-decoration: none;
    aspect-ratio: 1/1;

    &:focus {
      outline: none;
    }
  }
`;

const StyledImage = styled(GatsbyImage)``;

const ImgWrapper = styled.div`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const Title = styled.h3`
  position: relative;
  margin: 0;
  font-family: var(--earth-fm--font-family--jakarta);
  font-size: ${cssClampValue(13, 32)};
  font-weight: 600;
  line-height: 1.5;
  color: #fff;
`;

interface Props {
  data: Queries.PlaylistCardDataFragment;
  className?: string;
  removeLink?: boolean;
  ignoreTitleOnScreenReader?: boolean;
}

export default function PlaylistCard({
  data: { title, uri, featuredImage, playlistSettings },
  className = undefined,
  removeLink = false,
  ignoreTitleOnScreenReader = false,
}: Props) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const { width: cardWidth } = useElementSize(wrapperRef);
  const playlistName = playlistSettings?.playlistName || title;

  if (!uri || !title) {
    return null;
  }

  const image = featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
    ? getImage(featuredImage.node.localFile.childImageSharp.gatsbyImageData)
    : null;

  return (
    <Wrapper className={className} ref={wrapperRef}>
      {removeLink ? (
        <div className={className ? `${className}__link` : ''}>
          <ImgWrapper>
            {image ? (
              <StyledImage image={image} alt={getSeoFriendlyAlt(title)} />
            ) : (
              <DefaultPlaylistImage alt={getSeoFriendlyAlt(title)} />
            )}
          </ImgWrapper>
          <Title
            className={className ? `${className}__title` : ''}
            style={{ fontSize: Math.max(13, Math.min(32, cardWidth * 0.125)) }}
            aria-hidden={ignoreTitleOnScreenReader}
          >
            {playlistName}
          </Title>
        </div>
      ) : (
        <Link to={uri} className={className ? `${className}__link` : ''}>
          <ImgWrapper>
            {image ? (
              <StyledImage image={image} alt={getSeoFriendlyAlt(title)} />
            ) : (
              <DefaultPlaylistImage alt={getSeoFriendlyAlt(title)} />
            )}
          </ImgWrapper>
          <Title
            className={className ? `${className}__title` : ''}
            style={{ fontSize: Math.max(13, Math.min(32, cardWidth * 0.125)) }}
            aria-hidden={ignoreTitleOnScreenReader}
          >
            {playlistName}
          </Title>
        </Link>
      )}
    </Wrapper>
  );
}

export const PlaylistCardDataFragment = graphql`
  fragment PlaylistCardData on WpPlaylist {
    id: databaseId
    title
    date
    playlistSettings {
      playlistName
      hideFromProduction
    }
    uri
    featuredImage {
      node {
        localFile {
          childImageSharp {
            gatsbyImageData(placeholder: DOMINANT_COLOR, width: 512)
          }
        }
      }
    }
  }
`;
